/**
 * Utility functions for formatting numbers in exams and daily quizzes results
 * Location: src/utils/numberFormatter.js
 */

/**
 * Formats accuracy percentage values
 * @param {number|string} value - The value to format
 * @returns {string} Formatted percentage (e.g., "38.67", "80", "0")
 */
export const formatAccuracy = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  // First, round to 2 decimal places
  const roundedValue = Math.round(Number(value) * 100) / 100;

  // If it is an integer after rounding, display it without decimals
  if (roundedValue % 1 === 0) {
    return roundedValue.toString();
  }
  // If it has decimals, display them with up to 2 decimal places (without unnecessary zeros)
  return parseFloat(roundedValue.toFixed(2)).toString();
};

/**
 * Formats number values (typically for points)
 * @param {number|string} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  return Number(value).toFixed(decimals);
};

/**
 * Formats time strings and validates format
 * @param {string} timeString - Time string in various formats (MM:SS, HH:MM:SS, etc.)
 * @returns {string} Formatted time string
 */
export const formatTime = (timeString) => {
  if (!timeString || typeof timeString !== 'string') {
    return '00:00';
  }

  const parts = timeString.split(':');
  if (parts.length < 2) {
    return '00:00';
  }

  // Validate each part is a number
  const isValidTime = parts.every((part) => /^\d+$/.test(part));
  if (!isValidTime) {
    return '00:00';
  }

  // Return the time string as-is if valid
  return timeString;
};

/**
 * Converts milliseconds to a human-readable hours and minutes text
 * @param {number} millis - Time in milliseconds
 * @returns {string} Formatted string (e.g., "2 hours and 30 minutes", "3 hours")
 */
export const millisToHoursAndMinutesText = (millis) => {
  const totalMinutes = Math.floor(millis / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  }

  return parts.join(' and ');
};
